import { filterType } from '../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
  ListFilterResponseProperties,
} from '../interface/filter.interface';

/**
 * Function to realign list filter response
 * @param {any} filters filters
 */
function ConfigureListFilterResponse(filters: ListFilterResponseProperties[]) {
  const updatedFilters: CommonFilterProperties[] = filters.map((filter) => {
    let singleFilter;
    if (
      filter.subFilters.type === filterType.DATE_RANGE ||
      filter.subFilters.type === filterType.TIME_RANGE
    ) {
      singleFilter = {
        name: filter.label,
        key: `${filter.value1}_${filter.value2}`,
        value: '',
        key1: filter.value1,
        value1: '',
        key2: filter.value2,
        value2: '',
        type: filter.subFilters.type,
        listOfValue: [],
        applyFilter: false,
        enable: true,
      };
    } else if (filter.subFilters.type === filterType.ANGLE_BRACKETS) {
      singleFilter = {
        name: filter.label,
        key: `${filter.value}Value-${filter.value}Operator`,
        value: '',
        key1: `${filter.value}Value`,
        value1: '',
        key2: `${filter.value}Operator`,
        value2: '',
        type: filter.subFilters.type,
        listOfValue: Array.isArray(filter?.subFilters?.options)
          ? filter?.subFilters?.options?.map((option) =>
              typeof option === 'string' ? { label: option, value: option } : option,
            )
          : [],
        applyFilter: false,
        enable: true,
      };
    } else {
      singleFilter = {
        name: filter.label,
        key: filter.value,
        type: filter.subFilters.type,
        value:
          filter.subFilters.type === filterType.CHECKBOX ||
          filterType.MULTISELECT ||
          filterType.SELECT_FIELD
            ? []
            : '',
        listOfValue: Array.isArray(filter?.subFilters?.options)
          ? filter?.subFilters?.options?.map((option) =>
              typeof option === 'string' ? { label: option, value: option } : option,
            )
          : [],
        applyFilter: false,
        enable: true,
      };
    }
    return singleFilter;
  });
  return updatedFilters;
}

/**
 * Function to update a value to filter
 * @param {any} existingFilters existing filters
 * @param {any} updatedValue updated value
 */
function UpdateCommonFilter(
  existingFilters: CommonFilterProperties[],
  updatedValue: FilterUpdateProperties,
) {
  let isFilterUpdated = false;
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters.map((filter) => {
    let updatedFilter = filter;
    if (updatedValue.key === filterType.DATE_RANGE) {
      if (updatedValue.subKey === updatedFilter.key1) {
        updatedFilter = {
          ...updatedFilter,
          value1: updatedValue.value.toString(),
          applyFilter: true,
        };
        isFilterUpdated = true;
      } else if (updatedValue.subKey === updatedFilter.key2) {
        updatedFilter = {
          ...updatedFilter,
          value2: updatedValue.value.toString(),
          applyFilter: true,
        };
        isFilterUpdated = true;
      } else {
        console.error('SubKey not found', updatedValue.subKey);
      }
    } else if (updatedValue.key === filterType.ANGLE_BRACKETS) {
      if (updatedValue.subKey === updatedFilter.key1) {
        updatedFilter = {
          ...updatedFilter,
          value1: updatedValue.value.toString(),
          applyFilter: Boolean(updatedValue.value && updatedFilter.value2),
        };
        isFilterUpdated = Boolean(updatedValue.value && updatedFilter.value2);
      } else if (updatedValue.subKey === updatedFilter.key2) {
        updatedFilter = {
          ...updatedFilter,
          value2: updatedValue.value.toString(),
          applyFilter: Boolean(updatedValue.value && updatedFilter.value1),
        };
        isFilterUpdated = Boolean(updatedValue.value && updatedFilter.value1);
      } else {
        console.error('SubKey not found', updatedValue.subKey);
      }
    } else if (filter.key === updatedValue.key) {
      isFilterUpdated = true;
      switch (filter.type) {
        case filterType.CHECKBOX:
          if (Array.isArray(filter.value)) {
            const valueExists = filter.value.includes(updatedValue.value.toString());
            updatedFilter = {
              ...filter,
              value: valueExists
                ? filter.value.filter((value) => value !== updatedValue.value.toString())
                : [...filter.value, updatedValue.value.toString()],
            };
          }
          if (updatedFilter.value.length > 0) {
            updatedFilter.applyFilter = true;
          } else {
            updatedFilter.applyFilter = false;
          }
          break;
        case filterType.MULTISELECT:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue?.value,
            valueToShow: updatedValue?.valueToShow,
            applyFilter: updatedValue?.value?.length > 0,
          };
          break;
        default:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue.value,
            applyFilter: updatedValue.value !== '',
          };
          break;
      }
    }
    return updatedFilter;
  });

  return { updatedCommonFilter, isFilterUpdated };
}

/**
 * Function to update a value to filter
 * @param {any} existingFilters - Existing filters
 * @param {any} updatedValue - Updated value
 * @returns {object} The updated filters
 */
function ReplaceValueInFilter(
  existingFilters: CommonFilterProperties[],
  updatedValue: FilterUpdateProperties,
) {
  let isFilterUpdated = false;
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters.map((filter) => {
    let updatedFilter = filter;
    if (filter.key === updatedValue.key) {
      isFilterUpdated = true;
      switch (filter.type) {
        case filterType.CHECKBOX:
          if (Array.isArray(filter.value)) {
            updatedFilter = {
              ...filter,
              value:
                typeof updatedValue.value === 'string' ? [updatedValue.value] : updatedValue.value,
            };
          }
          if (updatedFilter.value.length > 0) {
            updatedFilter.applyFilter = true;
          } else {
            updatedFilter.applyFilter = false;
          }
          break;
        case filterType.MULTISELECT:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue?.value,
            valueToShow: updatedValue?.valueToShow,
            applyFilter: updatedValue?.value?.length > 0,
          };
          break;
        default:
          updatedFilter = {
            ...updatedFilter,
            value: updatedValue.value,
            applyFilter: updatedValue.value !== '',
          };
          break;
      }
    }
    return updatedFilter;
  });
  return { updatedCommonFilter, isFilterUpdated };
}

/**
 * Function to generate params based on filters array of object
 * @param {any} filters filters
 */
function GetParametersFromCommonFilters(filters: CommonFilterProperties[]) {
  const filterParameters: any = {};
  filters.forEach((filter: CommonFilterProperties) => {
    if (filter.applyFilter && filter.type === filterType.DATE_RANGE) {
      if (filter.value1 !== '') {
        filterParameters[filter.key1 || 'startDate'] = filter.value1;
      }
      if (filter.value2 !== '') {
        filterParameters[filter.key2 || 'endDate'] = filter.value2;
      }
    } else if (filter.applyFilter && filter.type === filterType.TIME_RANGE) {
      if (filter.value1 !== '') {
        filterParameters[filter.key1 || 'startTime'] = filter.value1;
      }
      if (filter.value2 !== '') {
        filterParameters[filter.key2 || 'endTime'] = filter.value2;
      }
    } else if (filter.applyFilter && filter.type === filterType.ANGLE_BRACKETS) {
      if (filter.value1 !== '') {
        filterParameters[filter.key1 || ''] = filter.value1;
      }
      if (filter.value2 !== '') {
        filterParameters[filter.key2 || ''] = filter.value2;
      }
    } else if (filter.applyFilter) {
      filterParameters[filter.key] = filter.value;
    }
  });
  return filterParameters;
}

/**
 * Function to remove a applied filter and return to initial state
 * @param {any} existingFilters existing filters
 * @param {any} key key
 */
function RemoveSingleFilterFromList(existingFilters: CommonFilterProperties[], key: string) {
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters.map((filter) => {
    let updatedFilter = filter;
    if (filter.key === key) {
      switch (filter.type) {
        case filterType.CHECKBOX:
          updatedFilter = {
            ...filter,
            applyFilter: false,
            value: [],
          };
          break;
        case filterType.DATE_RANGE:
        case filterType.TIME_RANGE:
        case filterType.ANGLE_BRACKETS:
          updatedFilter = {
            ...filter,
            applyFilter: false,
            value1: '',
            value2: '',
          };
          break;
        default:
          updatedFilter = {
            ...updatedFilter,
            value: '',
            applyFilter: false,
          };
          break;
      }
    }
    return updatedFilter;
  });

  return updatedCommonFilter;
}

/**
 * Function to remove a applied filter and return to initial state
 * @param {any} existingFilters existing filters
 */
function ClearFilters(existingFilters: CommonFilterProperties[]) {
  // Clear all filters except those that should not be cleared
  const updatedCommonFilter: CommonFilterProperties[] = existingFilters?.map((filter) => {
    let updatedFilter = filter;
    if (filter.disableClearFilter) {
      return filter; // Skip filters that should not be cleared
    }
    switch (filter.type) {
      case filterType.CHECKBOX:
        updatedFilter = {
          ...filter,
          applyFilter: false,
          value: [],
        };
        break;
      case filterType.DATE_RANGE:
      case filterType.TIME_RANGE:
      case filterType.ANGLE_BRACKETS:
        updatedFilter = {
          ...filter,
          applyFilter: false,
          value1: '',
          value2: '',
        };
        break;
      default:
        updatedFilter = {
          ...updatedFilter,
          value: '',
          applyFilter: false,
        };
        break;
    }
    return updatedFilter;
  });

  return updatedCommonFilter;
}

/**
 * Function that will reset all filters and add a single filter.
 * If the filter is not found in the existing list, it creates a placeholder.
 * @param {any} existingFilters The current list of filters.
 * @param {any} updatedValue The filter to be added/updated.
 * @returns {any} Object containing the updated filters array and a boolean indicating if it was updated.
 */
const RestFilterAndAddSingleFilter = (
  existingFilters: CommonFilterProperties[],
  updatedValue: FilterUpdateProperties,
) => {
  const unappliedFilterList: CommonFilterProperties[] = ClearFilters(existingFilters);
  const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
    unappliedFilterList,
    updatedValue,
  );

  if (!isFilterUpdated && updatedValue.key) {
    // If filter not found in existing list, add it as a placeholder
    const newFilter: CommonFilterProperties = {
      name: updatedValue.key,
      key: updatedValue.key,
      type: filterType.TEXT, // Default, will be updated when full list is loaded
      value: updatedValue.value,
      applyFilter: true,
      enable: true,
      FEAddedFilter: true,
    };
    return {
      updatedCommonFilter: [...unappliedFilterList, newFilter],
      isFilterUpdated: true,
    };
  }

  return { updatedCommonFilter, isFilterUpdated: true };
};

/**
 * Function to merge existing filters with incoming ones from API.
 * Preserves user-applied filter values from the existing state.
 * @param {any} existing Array of current filters in Redux state (may contain placeholders)
 * @param {any} incoming Array of fresh filters from the API
 * @returns {any} Merged array of filters
 */
function MergeCommonFilters(
  existing: CommonFilterProperties[],
  incoming: CommonFilterProperties[],
) {
  const incomingKeys = new Set(incoming.map((filter) => filter.key));
  const merged = incoming.map((filter) => {
    const existingFilter = existing.find((f) => f.key === filter.key);
    if (existingFilter) {
      return {
        ...filter,
        value: existingFilter.value,
        applyFilter: existingFilter.applyFilter,
        value1: existingFilter.value1,
        value2: existingFilter.value2,
        valueToShow: existingFilter.valueToShow,
      };
    }
    return filter;
  });

  const extras = existing.filter((filter) => !incomingKeys.has(filter.key));
  return [...extras, ...merged];
}

export {
  UpdateCommonFilter,
  ConfigureListFilterResponse,
  GetParametersFromCommonFilters,
  RemoveSingleFilterFromList,
  ClearFilters,
  ReplaceValueInFilter,
  RestFilterAndAddSingleFilter,
  MergeCommonFilters,
};
